import React from 'react';
import './ApiDocs.css';

function ITMOTMCalculation() {
  return (
    <div className="api-docs">
      <div className="api-docs-container">
        <main className="api-docs-content" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="endpoint-section">
            <h2>Option Moneyness Berekening (ITM/OTM/ATM)</h2>
            <p className="endpoint-description">
              Dit document beschrijft hoe de moneyness (ITM/OTM/ATM) status van opties wordt berekend, inclusief de premie die is betaald of ontvangen.
            </p>

            <h3>Data Velden</h3>
            <div className="method-card">
              <h4>Vereiste velden voor berekening:</h4>
              <ul>
                <li><strong>strike</strong> (string): Strike price van de optie → wordt geparsed naar float</li>
                <li><strong>put_call</strong> (string): "C" voor Call, "P" voor Put</li>
                <li><strong>underlying_price</strong> (decimal): Huidige prijs van het underlying asset</li>
                <li><strong>open_price</strong> (decimal): Premie betaald (long) of ontvangen (short)</li>
                <li><strong>cost_basis_price</strong> (decimal): Fallback voor premie als <code>open_price</code> niet beschikbaar is</li>
                <li><strong>position</strong> (decimal): 
                  <ul>
                    <li>Positief = LONG optie (premie betaald)</li>
                    <li>Negatief = SHORT optie (premie ontvangen)</li>
                  </ul>
                </li>
                <li><strong>asset_category</strong> (string): Moet "OPT" zijn voor opties</li>
              </ul>
            </div>

            <h3>Berekeningen</h3>

            <div className="method-card">
              <h4>Stap 1: Intrinsieke Waarde Bepalen</h4>
              <p><strong>Voor CALL opties:</strong></p>
              <pre><code>intrinsieke_waarde = max(0, underlying_price - strike)</code></pre>
              <p><strong>Voor PUT opties:</strong></p>
              <pre><code>intrinsieke_waarde = max(0, strike - underlying_price)</code></pre>
            </div>

            <div className="method-card">
              <h4>Stap 2: Huidige Waarde Bepalen</h4>
              <p><strong>Gebruik mark_price als beschikbaar:</strong></p>
              <ul>
                <li><code>mark_price</code> = huidige marktprijs van de optie (intrinsieke waarde + tijdswaarde)</li>
                <li>Als <code>mark_price</code> niet beschikbaar is, gebruik <code>intrinsieke_waarde</code></li>
              </ul>
              <p><strong>Voor OTM opties:</strong></p>
              <ul>
                <li>Intrinsieke waarde = 0, maar mark_price kan nog steeds &gt; 0 zijn (tijdswaarde)</li>
                <li>Daarom gebruiken we altijd <code>mark_price</code> als die beschikbaar is</li>
              </ul>
            </div>

            <div className="method-card">
              <h4>Stap 3: Premie Bepalen</h4>
              <p><strong>Voor LONG opties (position &gt; 0):</strong></p>
              <ul>
                <li>Premie betaald = <code>open_price</code> (of <code>cost_basis_price</code> als fallback)</li>
                <li>Dit is geld dat is uitgegeven om de optie te kopen</li>
              </ul>
              <p><strong>Voor SHORT opties (position &lt; 0):</strong></p>
              <ul>
                <li>Premie ontvangen = <code>open_price</code> (of <code>cost_basis_price</code> als fallback)</li>
                <li>Dit is geld dat is ontvangen voor het verkopen van de optie</li>
              </ul>
            </div>

            <div className="method-card">
              <h4>Stap 4: Netto Waarde Bepalen (incl. Premie)</h4>
              <p><strong>Voor LONG opties:</strong></p>
              <pre><code>netto_waarde = huidige_waarde (mark_price of intrinsieke) - premie_betaald</code></pre>
              <p><strong>Voor SHORT opties:</strong></p>
              <pre><code>netto_waarde = premie_ontvangen - huidige_waarde (mark_price of intrinsieke)</code></pre>
            </div>

            <div className="method-card">
              <h4>Stap 5: Status Bepaling</h4>
              
              <h5>ITM (In The Money)</h5>
              <p>Een optie is ITM als de intrinsieke waarde &gt; 0.</p>
              <ul>
                <li><strong>ITM (Groen):</strong> Intrinsieke waarde &gt; 0 EN netto waarde &gt; 0 - De optie heeft waarde EN na aftrek/toevoeging van premie is er nog steeds winst</li>
                <li><strong>ITM (Rood):</strong> Intrinsieke waarde &gt; 0 EN netto waarde &lt; 0 - De optie heeft waarde MAAR na aftrek/toevoeging van premie is er verlies</li>
                <li><strong>ITM (Oranje):</strong> Intrinsieke waarde &gt; 0 EN netto waarde = 0 - Break-even situatie</li>
              </ul>

              <h5>OTM (Out The Money)</h5>
              <p>Een optie is OTM als de intrinsieke waarde = 0.</p>
              <ul>
                <li><strong>OTM (Groen):</strong> Intrinsieke waarde = 0 EN netto waarde &gt; 0 - Geen intrinsieke waarde, maar mark_price (tijdswaarde) is hoger dan premie betaald/ontvangen</li>
                <li><strong>OTM (Rood):</strong> Intrinsieke waarde = 0 EN netto waarde &lt; 0 - Geen intrinsieke waarde, en mark_price (tijdswaarde) is lager dan premie betaald/ontvangen</li>
                <li><strong>OTM (Grijs):</strong> Intrinsieke waarde = 0 EN netto waarde = 0 - Break-even of geen mark_price data beschikbaar</li>
              </ul>

              <h5>ATM (At The Money)</h5>
              <p>Een optie is ATM als de underlying price zeer dicht bij de strike price ligt.</p>
              <p><strong>Berekening:</strong></p>
              <pre><code>percentage_verschil = abs(underlying_price - strike) / strike</code></pre>
              <p>Als <code>percentage_verschil &lt; 0.005</code> (0.5%), dan is de optie ATM.</p>
              <ul>
                <li><strong>ATM (Oranje):</strong> Underlying price is zeer dicht bij strike price (±0.5%)</li>
              </ul>
            </div>

            <h3>Edge Cases</h3>
            <div className="method-card">
              <ul>
                <li><strong>Geen Underlying Price:</strong> Status: "N/A" (Grijs) - Kan geen berekening doen zonder huidige prijs</li>
                <li><strong>Geen Premie Data:</strong> Probeer eerst <code>open_price</code>, dan <code>cost_basis_price</code>, anders "N/A" (Grijs)</li>
                <li><strong>Geen Strike Price:</strong> Skip berekening (geen optie) - Alleen opties met <code>asset_category === 'OPT'</code> en <code>put_call</code> niet leeg worden berekend</li>
                <li><strong>Strike is String:</strong> Parse naar float voor berekening, als parsing faalt: "N/A" (Grijs)</li>
              </ul>
            </div>

            <h3>Voorbeelden</h3>
            
            <div className="method-card">
              <h4>Voorbeeld 1: Long Call ITM (Groen)</h4>
              <ul>
                <li>Strike: 100</li>
                <li>Underlying: 105</li>
                <li>Premie betaald: 3</li>
                <li>Intrinsieke waarde: max(0, 105 - 100) = 5</li>
                <li>Netto: 5 - 3 = 2</li>
                <li>Status: <strong>ITM (Groen)</strong></li>
              </ul>
            </div>

            <div className="method-card">
              <h4>Voorbeeld 2: Long Call ITM (Rood)</h4>
              <ul>
                <li>Strike: 100</li>
                <li>Underlying: 102</li>
                <li>Premie betaald: 3</li>
                <li>Intrinsieke waarde: max(0, 102 - 100) = 2</li>
                <li>Netto: 2 - 3 = -1</li>
                <li>Status: <strong>ITM (Rood)</strong></li>
              </ul>
            </div>

            <div className="method-card">
              <h4>Voorbeeld 3: Short Put OTM (Groen)</h4>
              <ul>
                <li>Strike: 100</li>
                <li>Underlying: 105</li>
                <li>Premie ontvangen: 2</li>
                <li>Intrinsieke waarde: max(0, 100 - 105) = 0</li>
                <li>Netto: 2 - 0 = 2</li>
                <li>Status: <strong>OTM (Groen)</strong></li>
              </ul>
            </div>

            <div className="method-card">
              <h4>Voorbeeld 4: Long Put OTM (Rood)</h4>
              <ul>
                <li>Strike: 100</li>
                <li>Underlying: 105</li>
                <li>Premie betaald: 2</li>
                <li>Intrinsieke waarde: max(0, 100 - 105) = 0</li>
                <li>Netto: 0 - 2 = -2</li>
                <li>Status: <strong>OTM (Rood)</strong></li>
              </ul>
            </div>

            <div className="method-card">
              <h4>Voorbeeld 5: ATM</h4>
              <ul>
                <li>Strike: 100</li>
                <li>Underlying: 100.3</li>
                <li>Percentage verschil: abs(100.3 - 100) / 100 = 0.003 (0.3%)</li>
                <li>Status: <strong>ATM (Oranje)</strong></li>
              </ul>
            </div>

            <h3>Kleuren Schema</h3>
            <div className="method-card">
              <ul>
                <li><strong>Groen:</strong> ITM of OTM met winst (netto waarde &gt; 0 na premie)</li>
                <li><strong>Rood:</strong> ITM of OTM met verlies (netto waarde &lt; 0 na premie)</li>
                <li><strong>Oranje:</strong> ATM (at the money) of break-even (netto waarde = 0)</li>
                <li><strong>Grijs:</strong> N/A (geen data beschikbaar) of break-even zonder mark_price</li>
              </ul>
            </div>

            <h3>Implementatie Locatie</h3>
            <div className="method-card">
              <ul>
                <li><strong>Frontend:</strong> <code>frontend/src/OpenPositions.js</code></li>
                <li><strong>Functie:</strong> <code>calculateMoneyness(position)</code></li>
                <li><strong>Weergave:</strong> Nieuwe kolom "Moneyness" in de opties tabel met badge</li>
              </ul>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default ITMOTMCalculation;

